import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from './firebase-init';

const JOBS_COLLECTION = 'cutlistJobs';

// ── Create a new job ──────────────────────────────────────────────────────────
export async function createJob(jobData) {
  const ref = await addDoc(collection(db, JOBS_COLLECTION), {
    ...jobData,
    status: 'estimate',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

// ── Get a single job by ID ────────────────────────────────────────────────────
export async function getJob(jobId) {
  if (!jobId) return null;
  // Retry up to 3 times with backoff. Handles transient Firestore cache misses
  // and auth-refresh races where the first fetch returns "not exists" before
  // live data lands. Real "deleted" docs stay null after all retries.
  const ref = doc(db, JOBS_COLLECTION, jobId);
  let lastErr = null;
  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      const snap = await getDoc(ref);
      if (snap.exists()) return { id: snap.id, ...snap.data() };
    } catch (e) {
      lastErr = e;
    }
    // Wait 250ms, 500ms before next try
    if (attempt < 2) await new Promise(r => setTimeout(r, 250 * (attempt + 1)));
  }
  if (lastErr) throw lastErr;
  return null;
}

// ── Get all jobs, newest first ────────────────────────────────────────────────
export async function getAllJobs() {
  const q = query(
    collection(db, JOBS_COLLECTION),
    orderBy('createdAt', 'desc')
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

// ── Get jobs by status ────────────────────────────────────────────────────────
export async function getJobsByStatus(status) {
  const q = query(
    collection(db, JOBS_COLLECTION),
    where('status', '==', status),
    orderBy('createdAt', 'desc')
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

// ── Search jobs by customer name ──────────────────────────────────────────────
export async function searchJobsByCustomer(name) {
  const all = await getAllJobs();
  const lower = name.toLowerCase();
  return all.filter(j =>
    (j.customerName || '').toLowerCase().includes(lower)
  );
}

// ── Update job fields ─────────────────────────────────────────────────────────
export async function updateJob(jobId, updates) {
  await updateDoc(doc(db, JOBS_COLLECTION, jobId), {
    ...updates,
    updatedAt: serverTimestamp(),
  });
}

// ── Update job status ─────────────────────────────────────────────────────────
// Valid statuses: 'estimate' | 'sold' | 'in_production' | 'ready' | 'installed'
export async function updateJobStatus(jobId, status) {
  await updateDoc(doc(db, JOBS_COLLECTION, jobId), {
    status,
    updatedAt: serverTimestamp(),
  });
}

// ── Delete a job ──────────────────────────────────────────────────────────────
export async function deleteJob(jobId) {
  // Also delete linked scheduler job if exists
  try {
    const snap = await getDoc(doc(db, JOBS_COLLECTION, jobId));
    if (snap.exists()) {
      const twpJobId = snap.data().twpJobId;
      if (twpJobId) {
        await deleteDoc(doc(db, 'jobs', twpJobId));
      }
    }
    await deleteDoc(doc(db, JOBS_COLLECTION, jobId));
  } catch(e) {
    console.error('Error deleting cutlist job:', e);
    throw e;
  }
}

// ── Status config (shared across UI) ─────────────────────────────────────────
export const JOB_STATUSES = [
  { key: 'estimate',      label: 'Estimate',      color: '#2563eb', bg: 'rgba(37,99,235,0.1)'   },
  { key: 'sold',          label: 'Sold',           color: '#16a34a', bg: 'rgba(22,163,74,0.1)'   },
  { key: 'in_production', label: 'In Production',  color: '#d97706', bg: 'rgba(217,119,6,0.1)'   },
  { key: 'ready',         label: 'Ready',          color: '#7c3aed', bg: 'rgba(124,58,237,0.1)'  },
  { key: 'installed',     label: 'Installed',      color: '#059669', bg: 'rgba(5,150,105,0.1)'   },
];

export function getStatusConfig(key) {
  return JOB_STATUSES.find(s => s.key === key) || JOB_STATUSES[0];
}

// ── Mark job as sent/unsent to glass or flex screen supplier ──────────────────
export async function updateJobSupplierStatus(jobId, field, value) {
  // field is 'glassSent' or 'flexSent', value is true or false
  await updateDoc(doc(db, JOBS_COLLECTION, jobId), {
    [field]: value,
    updatedAt: serverTimestamp(),
  });
}
